import * as CurrentTime from '@/models/current-time/functions'
import { convertFromCartItemToSubscriptionCartItem } from '@/models/order/subscription/subscription-cart-item'

import { isDisplayItem } from '@/models/cart/group/group'
import * as DateFns from 'date-fns'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { InvalidSalesPeriodError } from './errors'
import { afterSales, beforeSales, CartItem, inSales, SalesPeriodState } from './types'

/**
 * 購入が可能な商品であるかどうかの判断基準を定義している.
 * - fkuが公開中
 * - 在庫あり
 * - 購入上限 > 0
 * - 販売中
 * - セット商品の場合、親子が揃っている
 *
 * NOTE: 親商品である場合は、正しく子商品がカート内に存在するかどうかも判定している。
 * 本来は親子商品は一つの集約としてモデル化される必要があるが、現在は集約の要素である親・子がそのままカートリソースとして扱われているため、
 * ここで疑似的に集約としての制約をフィルタとしてかけている。
 *
 * @param item 判定しようとしている個別のカート内商品
 * @param whole カート内商品全体. 例えばセット商品などの場合、「親子」という複数のカート内商品に基づいて判断するため.
 */
export const filterValidItemAsAggregate = (item: CartItem, whole: CartItem[]): boolean => {
  const itemResult = filterValidItem(item)

  if (!item.isParent) {
    return itemResult
  }

  const childId = _.get(item.childItem, 'id')
  const child = whole.find(maybeChild => childId === maybeChild.rootId)

  if (!child) {
    return false
  }

  const childResult = filterValidItemAsAggregate(child, whole)

  return itemResult && childResult
}

export const filterValidItem = (item: CartItem): boolean => {
  const IN_STORE = 4

  if (!item.reasonForCannotBePurchased.isEmpty) {
    return false
  }

  if (!item.isFkuPublic || item.maxSellCount === undefined || item.stock === undefined) {
    return false
  }

  /* 注文可能である条件は「通常商品」と「予約商品」とで異なる */
  if (!canPurchaseItem(item) && !canPurchasePreOrderItem(item)) {
    return false
  }

  return item.maxSellCount > 0  && item.stock > 0 && item.salesStatus === IN_STORE
}

export function canPurchaseItem(item: CartItem): boolean {
  const salesPeriod = salesPeriodStateOf(item)
  if (salesPeriod.isLeft()) {
    console.error(salesPeriod.value)

    return false
  }

  return salesPeriod.value.isOnSales
}

export const canPurchasePreOrderItem = (item: CartItem): boolean => {
  const salesPeriod = salesPeriodStateOf(item)
  if (salesPeriod.isLeft()) {
    console.error(salesPeriod.value)

    return false
  }

  return salesPeriod.value.isBeforeStart && item.isPreSales
}

export const salesPeriodStateOf = (
  item: CartItem
): Either<InvalidSalesPeriodError, SalesPeriodState> => {
  const startAt = item.salesStartAt
  const endAt = item.salesEndAt
  const now = CurrentTime.create().now

  if (DateFns.isBefore(endAt, startAt)) {
    return  new Left(
      new InvalidSalesPeriodError(
        'The sales end datetime is set before the start datetime.')
    )
  }

  const currentIsBeforeStart = DateFns.isBefore(now, startAt)
  const currentIsEqualStart = DateFns.isEqual(now, startAt)
  const currentIsAfterStart = DateFns.isAfter(now, startAt)
  const currentIsBeforeEnd = DateFns.isBefore(now, endAt)
  const currentIsEqualEnd = DateFns.isEqual(now, endAt)

  if (currentIsBeforeStart) {
    return new Right(beforeSales)
  }
  if ((currentIsEqualStart || currentIsAfterStart)
      && (currentIsBeforeEnd || currentIsEqualEnd)) {
    return new Right(inSales)
  }

  return new Right(afterSales)
}

/*
 * 2021/09/02 時点ではやむをえず、こうした関数を導入することでadhocに
 * 各ページでフィルタリングする形を取らざるを得ない.
 *
 * 最終的には、単発注文/定期購入それぞれの入口時点を互いに分離して、
 * それぞれで必要なI/Fを用意し、それぞれで必要なデータのみを振り分けるようにすることが
 * 望ましいと思われる.
 *
 * このような対応を取らざるを得ない理由として、そもそも既存の実装が
 * 「入り口では全部受け入れて、各ページで都度絞り込み」というadhocを前提とした形になっているため.
 * このadhocさを取り除こうとすると、つまるところカート内商品を参照する全ページに手が入る.
 */

/**
 * 単発注文として購入可能な商品.
 *
 * 最終的には、そもそもこの関数を都度コンポーネントで使わなくて良いようにすべき.
 *
 * @deprecated 将来的に廃止
 * @param item
 */
export const isSinglePurchasable = (item: CartItem) => {
  return !(
    (item.removed || false) || isSubscriptionItem(item)
  )
}
export const isSubscriptionItem = (item: CartItem): boolean =>
  convertFromCartItemToSubscriptionCartItem(item).isRight()
/**
 * サブスクリプション申し込み可能な商品
 *
 * 本来、この関数でサブスクリプション申し込み不可能な商品を全て弾くべきだが、
 * この関数をadhocに使う(使わざるをえない)pageコンポーネント側で、
 * さらにadhocに変換を行っている(行わざるを得ない)状態であるため、
 * ここではあえて、削除済み以外は全件取得している.
 * 最終的には、そもそもこの関数を都度コンポーネントで使わなくて良いようにすべき.
 *
 * @deprecated 将来的に廃止
 * @see ApplySubscription.displayCartItems
 * @see ApplySubscription.cartItems
 */
export const isSubscriptionApplicable = (item: CartItem) => {
  return !(item.removed || false)
}

/**
 * 特定のスコープにおける合計金額を計算する
 *
 * TODO: 特定のスコープのカートアイテム「全体」を知っている「部分」を表現するCartItemの
 * コレクションオブジェクトを定義して、そこで合計計算を行うようにする
 * @see https://github.com/my-color/front/issues/7529#issuecomment-1703898042
 *
 * @param whole 特定のスコープのカート内商品全て・もしくは特定のスコープのカート内の単発注文商品全て
 */
export function totalPriceInScope(whole: CartItem[]): number {
  return _.sumBy(
    extractDisplayedPurchasableItems(whole),
    item => item.unitPrice * item.unitCount
  )
}

/**
 * 特定のスコープにおける合計商品点数を計算する
 *
 * TODO: 特定のスコープのカートアイテム「全体」を知っている「部分」を表現するCartItemの
 * コレクションオブジェクトを定義して、そこで合計計算を行うようにする
 * @see https://github.com/my-color/front/issues/7529#issuecomment-1703898042
 *
 * TODO: 「合計数」のコンテクスト境界と、コンテクスト毎のモデルを精査
 * TODO: 「合計数」の算出方法を集約
 *
 * カート画面での「合計数」の算出方法を本来はコレクションオブジェクトに集約したいが、
 * カート画面の「合計数」算出に要求されているのが
 * - 個数計算の基準となるのは isDisplayItem = true の商品 -> カート画面に表示される商品
 * - filterValidItemAsAggregate に必要なのは、 isDisplayItem でフィルタリングされる前の全カート内商品
 * であり、単に配列をラップしているだけの現状のコレクションオブジェクトでは吸収しきれない.
 *
 * そのため、一旦はコレクションオブジェクトの外側で別途個数を算出しておく.
 *
 * @param whole 特定のスコープのカート内商品全て・もしくは特定のスコープのカート内の単発注文商品全て
 */
export function totalCountInScope(whole: CartItem[]): number {
  return _.sumBy(
    extractDisplayedPurchasableItems(whole),
    item => item.unitCount
  )
}

/**
 * カート内商品として表示される、購入可能な商品を抽出する
 *
 * @param whole 特定のスコープのカート内商品全て・もしくは特定のスコープのカート内の単発注文商品全て
 */
export function extractDisplayedPurchasableItems(whole: CartItem[]): CartItem[] {

  return whole.filter(item =>
    // TODO: CartItemが単発注文のみを扱うようになった場合、isSinglePurchasable を削除する
    isSinglePurchasable(item) &&

    /**
     * removedプロパティがtrueのアイテムの除外は、store から removedを削除するまでの暫定処置
     * @see https://github.com/my-color/growthpalette-front/pull/7567#discussion_r1355314134
     */
    !item.removed &&

    /**
     * isDisplayItemは子商品とノベルティに対するフィルター。
     * 子商品とノベルティはカート内商品として表示されないため、
     * カート内の商品として個数はカウントされず、かつどちらも価格は0円という特徴がある。
     * これは、extractDisplayedPurchasableItems を totalPriceInScope と totalCountInScope の
     * 共通のフィルターとして使える前提条件となっていることに注意。
     * @see https://my-color.esa.io/posts/463
     * @see https://my-color.esa.io/posts/462
     */
    isDisplayItem(item) &&
    filterValidItemAsAggregate(item, whole)
  )
}
